import api, { unwrap } from './client';
import type { ApiResponse, PageResponse, SearchParams } from '@/types/api';
import type {
  FinAccount,
  FinBankAccount,
  FinBankReconciliation,
  FinBankTransaction,
  FinBudget,
  FinExpense,
  FinInvoice,
  FinJournalEntry,
  FinPayment,
  FinFinancialPeriod,
  FinVendor,
  FinanceDashboardSummary,
  FinancialReport,
  TrialBalanceLine,
  BudgetVarianceRow,
  AgingBucket,
  AccountingEvent,
  FinPostingRule,
  FinLegalEntity,
  FinAccountsPayable,
  FinPaymentRun,
  TreasurySummary,
  FinDeferredRevenue,
  FinIntercompanyTransaction,
  FinWorkflowDefinition,
  FinDunningNotice,
  FinTaxGroup,
  FinTax,
  FinBankFeedConfig,
  FinConsolidationRun,
  FinEliminationEntry,
  FinCustomReport,
} from '@/types/finance';

export const financeApi = {
  dashboardSummary: () =>
    unwrap(api.get<ApiResponse<FinanceDashboardSummary>>('/v1/finance/dashboard/summary')),

  createAccount: (body: {
    accountCode: string;
    accountName: string;
    accountType: string;
    parentAccountId?: number;
    currencyCode?: string;
    active?: boolean;
  }) => unwrap(api.post<ApiResponse<FinAccount>>('/v1/finance/accounts', body)),

  listAccounts: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<FinAccount>>>('/v1/finance/accounts', { params })),

  postJournal: (body: {
    referenceType: string;
    referenceId?: number;
    transactionDate: string;
    description?: string;
    lines: Array<{
      accountId: number;
      debitAmount?: number;
      creditAmount?: number;
      currencyCode?: string;
      lineDescription?: string;
    }>;
  }) => unwrap(api.post<ApiResponse<FinJournalEntry>>('/v1/finance/journals', body)),

  listJournals: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<FinJournalEntry>>>('/v1/finance/journals', { params })),

  trialBalance: () =>
    unwrap(api.get<ApiResponse<TrialBalanceLine[]>>('/v1/finance/reports/trial-balance')),

  createInvoice: (body: {
    customerId?: number;
    invoiceNumber?: string;
    invoiceDate: string;
    dueDate?: string;
    subtotal?: number;
    taxAmount?: number;
    discountAmount?: number;
    totalAmount?: number;
    currencyCode?: string;
    notes?: string;
    lines?: Array<{
      lineDescription: string;
      quantity: number;
      unitPrice: number;
      taxAmount?: number;
    }>;
  }) => unwrap(api.post<ApiResponse<FinInvoice>>('/v1/finance/invoices', body)),

  getInvoice: (id: number) =>
    unwrap(api.get<ApiResponse<import('@/types/finance').FinInvoiceDetail>>(`/v1/finance/invoices/${id}`)),

  getInvoicePreviewHtml: async (id: number) => {
    const response = await api.get(`/v1/finance/invoices/${id}/print`, { responseType: 'text' });
    return response.data as string;
  },

  downloadInvoicePdf: async (id: number, invoiceNumber?: string) => {
    const response = await api.get(`/v1/finance/invoices/${id}/pdf`, { responseType: 'blob' });
    return { blob: response.data as Blob, filename: `${invoiceNumber ?? `invoice-${id}`}.pdf` };
  },

  openInvoicePrint: async (id: number) => {
    const response = await api.get(`/v1/finance/invoices/${id}/print`, { responseType: 'text' });
    const blob = new Blob([response.data as string], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  sendInvoice: (id: number, body?: { email?: string; message?: string }) =>
    unwrap(api.post<ApiResponse<import('@/types/finance').FinInvoiceSendResult>>(
      `/v1/finance/invoices/${id}/send`,
      body ?? {},
    )),

  listInvoices: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<FinInvoice>>>('/v1/finance/invoices', { params })),

  recordPayment: (params: { customerId: number; amount: number; paymentMethod?: string }) =>
    unwrap(api.post<ApiResponse<FinPayment>>('/v1/finance/payments', null, { params })),

  listPayments: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<FinPayment>>>('/v1/finance/payments', { params })),

  listBankAccounts: () =>
    unwrap(api.get<ApiResponse<FinBankAccount[]>>('/v1/finance/banking/accounts')),

  createBankAccount: (body: {
    accountName: string;
    bankName?: string;
    accountNumber?: string;
    currencyCode?: string;
    ledgerAccountId?: number;
    active?: boolean;
  }) => unwrap(api.post<ApiResponse<FinBankAccount>>('/v1/finance/banking/accounts', body)),

  importBankStatement: (bankAccountId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return unwrap(
      api.post<ApiResponse<{ imported: number }>>(
        `/v1/finance/banking/reconciliation/import?bankAccountId=${bankAccountId}`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      ),
    );
  },

  importOfxStatement: (bankAccountId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return unwrap(
      api.post<ApiResponse<{ imported: number }>>(
        `/v1/finance/banking/reconciliation/import-ofx?bankAccountId=${bankAccountId}`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      ),
    );
  },

  importMt940Statement: (bankAccountId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return unwrap(
      api.post<ApiResponse<{ imported: number }>>(
        `/v1/finance/banking/reconciliation/import-mt940?bankAccountId=${bankAccountId}`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      ),
    );
  },

  listBankFeeds: () =>
    unwrap(api.get<ApiResponse<FinBankFeedConfig[]>>('/v1/finance/enterprise/bank-feeds')),

  saveBankFeed: (body: {
    bankAccountId: number;
    feedType: string;
    feedName: string;
    autoImport?: boolean;
    configJson?: Record<string, unknown>;
  }) => unwrap(api.post<ApiResponse<FinBankFeedConfig>>('/v1/finance/enterprise/bank-feeds', body)),

  autoMatchTransactions: (bankAccountId: number) =>
    unwrap(
      api.post<ApiResponse<{ matched: number }>>(
        `/v1/finance/banking/reconciliation/auto-match?bankAccountId=${bankAccountId}`,
      ),
    ),

  manualMatchTransaction: (transactionId: number, paymentId: number) =>
    unwrap(
      api.post<ApiResponse<FinBankTransaction>>(
        `/v1/finance/banking/reconciliation/manual-match?transactionId=${transactionId}&paymentId=${paymentId}`,
      ),
    ),

  unmatchedTransactions: (bankAccountId: number) =>
    unwrap(
      api.get<ApiResponse<FinBankTransaction[]>>(
        `/v1/finance/banking/reconciliation/unmatched?bankAccountId=${bankAccountId}`,
      ),
    ),

  finalizeReconciliation: (bankAccountId: number, statementDate: string, statementBalance: number) =>
    unwrap(
      api.post<ApiResponse<FinBankReconciliation>>(
        '/v1/finance/banking/reconciliation/finalize',
        null,
        { params: { bankAccountId, statementDate, statementBalance } },
      ),
    ),

  listPendingApprovals: () =>
    unwrap(api.get<ApiResponse<import('@/types/finance').FinPendingApproval[]>>('/v1/finance/approvals/pending')),

  listBudgets: (fiscalYear?: number) =>
    unwrap(api.get<ApiResponse<FinBudget[]>>('/v1/finance/budgets', { params: { fiscalYear } })),

  createBudget: (body: {
    budgetName: string;
    budgetCode: string;
    fiscalYear: number;
    lines?: Array<{ accountId?: number; departmentId?: number; plannedAmount?: number }>;
  }) => unwrap(api.post<ApiResponse<FinBudget>>('/v1/finance/budgets', body)),

  budgetVariance: (fiscalYear: number) =>
    unwrap(api.get<ApiResponse<BudgetVarianceRow[]>>('/v1/finance/budgets/variance', { params: { fiscalYear } })),

  listExpenses: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<FinExpense>>>('/v1/finance/expenses', { params })),

  createExpense: (body: {
    employeeId?: number;
    expenseNumber: string;
    expenseDate: string;
    currencyCode?: string;
    categoryCode?: string;
    items: Array<{ description: string; amount: number }>;
  }) => unwrap(api.post<ApiResponse<FinExpense>>('/v1/finance/expenses', body)),

  approveExpense: (id: number) =>
    unwrap(api.post<ApiResponse<FinExpense>>(`/v1/finance/expenses/${id}/approve`)),

  profitAndLoss: () =>
    unwrap(api.get<ApiResponse<FinancialReport>>('/v1/finance/reports/profit-loss')),

  balanceSheet: () =>
    unwrap(api.get<ApiResponse<FinancialReport>>('/v1/finance/reports/balance-sheet')),

  cashFlow: () =>
    unwrap(api.get<ApiResponse<FinancialReport>>('/v1/finance/reports/cash-flow')),

  arAging: () =>
    unwrap(api.get<ApiResponse<FinancialReport>>('/v1/finance/reports/ar-aging')),

  exportReport: async (reportType: string, format: 'PDF' | 'EXCEL') => {
    const response = await api.get(`/v1/finance/reports/export`, {
      params: { reportType, format },
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  listTaxes: () => unwrap(api.get<ApiResponse<import('@/types/finance').FinTax[]>>('/v1/finance/setup/taxes')),

  calculateTax: (taxId: number, amount: number) =>
    unwrap(
      api.get<ApiResponse<number>>('/v1/finance/tax/calculate', {
        params: { taxId, amount },
      }),
    ),

  listAssets: () => unwrap(api.get<ApiResponse<import('@/types/finance').FinAsset[]>>('/v1/finance/assets')),

  runDepreciation: (periodMonth?: string) =>
    unwrap(
      api.post<ApiResponse<{ posted: number }>>('/v1/finance/assets/depreciation/run', null, {
        params: { periodMonth: periodMonth ?? new Date().toISOString().slice(0, 10) },
      }),
    ),

  createDraftJournal: (body: {
    referenceType: string;
    referenceId?: number;
    transactionDate: string;
    description?: string;
    lines: Array<{
      accountId: number;
      debitAmount?: number;
      creditAmount?: number;
      currencyCode?: string;
      lineDescription?: string;
    }>;
  }) => unwrap(api.post<ApiResponse<FinJournalEntry>>('/v1/finance/journals/draft', body)),

  approveJournal: (id: number) =>
    unwrap(api.post<ApiResponse<FinJournalEntry>>(`/v1/finance/journals/${id}/approve`)),

  postApprovedJournal: (id: number) =>
    unwrap(api.post<ApiResponse<FinJournalEntry>>(`/v1/finance/journals/${id}/post`)),

  listFiscalPeriods: () =>
    unwrap(api.get<ApiResponse<FinFinancialPeriod[]>>('/v1/finance/fiscal-periods')),

  lockFiscalPeriod: (id: number) =>
    unwrap(api.post<ApiResponse<FinFinancialPeriod>>(`/v1/finance/fiscal-periods/${id}/lock`)),

  listApVendors: () => unwrap(api.get<ApiResponse<FinVendor[]>>('/v1/finance/ap/vendors')),

  reopenFiscalPeriod: (id: number) =>
    unwrap(api.post<ApiResponse<FinFinancialPeriod>>(`/v1/finance/enterprise/fiscal-periods/${id}/reopen`)),

  arAgingBuckets: () =>
    unwrap(api.get<ApiResponse<AgingBucket[]>>('/v1/finance/enterprise/reports/ar-aging-buckets')),

  apAgingBuckets: () =>
    unwrap(api.get<ApiResponse<AgingBucket[]>>('/v1/finance/enterprise/reports/ap-aging-buckets')),

  listAccountingEvents: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<AccountingEvent>>>('/v1/finance/enterprise/accounting-events', { params })),

  getAccountingEvent: (id: number) =>
    unwrap(api.get<ApiResponse<AccountingEvent>>(`/v1/finance/enterprise/accounting-events/${id}`)),

  listPostingRules: () =>
    unwrap(api.get<ApiResponse<FinPostingRule[]>>('/v1/finance/enterprise/posting-rules')),

  listLegalEntities: () =>
    unwrap(api.get<ApiResponse<FinLegalEntity[]>>('/v1/finance/enterprise/legal-entities')),

  createLegalEntity: (body: {
    entityCode: string;
    entityName: string;
    countryCode?: string;
    baseCurrencyCode?: string;
    taxRegistrationNumber?: string;
    parentEntityId?: number;
    consolidationEntity?: boolean;
  }) => unwrap(api.post<ApiResponse<FinLegalEntity>>('/v1/finance/enterprise/legal-entities', body)),

  openPayables: () =>
    unwrap(api.get<ApiResponse<{ openAmount: number }>>('/v1/finance/enterprise/dashboard/payables')),

  listOpenPayables: () =>
    unwrap(api.get<ApiResponse<FinAccountsPayable[]>>('/v1/finance/enterprise/ap/open-payables')),

  listPaymentRuns: () =>
    unwrap(api.get<ApiResponse<FinPaymentRun[]>>('/v1/finance/enterprise/ap/payment-runs')),

  createPaymentRun: (bankAccountId?: number) =>
    unwrap(
      api.post<ApiResponse<FinPaymentRun>>('/v1/finance/enterprise/ap/payment-runs', null, {
        params: bankAccountId != null ? { bankAccountId } : undefined,
      }),
    ),

  approvePaymentRun: (id: number) =>
    unwrap(api.post<ApiResponse<FinPaymentRun>>(`/v1/finance/enterprise/ap/payment-runs/${id}/approve`)),

  postPaymentRun: (id: number) =>
    unwrap(api.post<ApiResponse<FinPaymentRun>>(`/v1/finance/enterprise/ap/payment-runs/${id}/post`)),

  treasurySummary: () =>
    unwrap(api.get<ApiResponse<TreasurySummary>>('/v1/finance/enterprise/treasury/summary')),

  cashForecast: (days = 30) =>
    unwrap(api.get<ApiResponse<Record<string, number>>>('/v1/finance/enterprise/treasury/cash-forecast', { params: { days } })),

  listDeferredRevenue: () =>
    unwrap(api.get<ApiResponse<FinDeferredRevenue[]>>('/v1/finance/enterprise/revenue/deferred')),

  createDeferredRevenue: (body: {
    sourceType: string;
    sourceId?: number;
    customerId?: number;
    totalAmount: number;
    startDate: string;
    endDate: string;
    description?: string;
  }) => unwrap(api.post<ApiResponse<FinDeferredRevenue>>('/v1/finance/enterprise/revenue/deferred', body)),

  recognizeDueRevenue: () =>
    unwrap(api.post<ApiResponse<{ recognized: number }>>('/v1/finance/enterprise/revenue/recognize-due')),

  listIntercompany: () =>
    unwrap(api.get<ApiResponse<FinIntercompanyTransaction[]>>('/v1/finance/enterprise/intercompany')),

  consolidationSummary: () =>
    unwrap(api.get<ApiResponse<FinancialReport>>('/v1/finance/enterprise/reports/consolidation')),

  listWorkflows: (entityType?: string) =>
    unwrap(api.get<ApiResponse<FinWorkflowDefinition[]>>('/v1/finance/enterprise/workflows', { params: { entityType } })),

  saveWorkflow: (body: Record<string, unknown>) =>
    unwrap(api.post<ApiResponse<FinWorkflowDefinition>>('/v1/finance/enterprise/workflows', body)),

  listDunningNotices: () =>
    unwrap(api.get<ApiResponse<FinDunningNotice[]>>('/v1/finance/enterprise/dunning/notices')),

  runDunning: () =>
    unwrap(api.post<ApiResponse<{ sent: number }>>('/v1/finance/enterprise/dunning/run')),

  createTax: (body: { taxCode: string; taxName: string; taxType: string; inclusive?: boolean }) =>
    unwrap(api.post<ApiResponse<FinTax>>('/v1/finance/enterprise/taxes', body)),

  listTaxGroups: () =>
    unwrap(api.get<ApiResponse<FinTaxGroup[]>>('/v1/finance/enterprise/tax/groups')),

  runDueRecurringJournals: () =>
    unwrap(api.post<ApiResponse<{ posted: number }>>('/v1/finance/enterprise/recurring-journals/run-due')),

  listConsolidationRuns: () =>
    unwrap(api.get<ApiResponse<FinConsolidationRun[]>>('/v1/finance/enterprise/consolidation/runs')),

  runConsolidationClose: (fiscalYear: number, fiscalPeriod: number, notes?: string) =>
    unwrap(
      api.post<ApiResponse<FinConsolidationRun>>('/v1/finance/enterprise/consolidation/close', null, {
        params: { fiscalYear, fiscalPeriod, notes },
      }),
    ),

  listConsolidationEliminations: (runId: number) =>
    unwrap(
      api.get<ApiResponse<FinEliminationEntry[]>>(
        `/v1/finance/enterprise/consolidation/runs/${runId}/eliminations`,
      ),
    ),

  listCustomReports: () =>
    unwrap(api.get<ApiResponse<FinCustomReport[]>>('/v1/finance/enterprise/custom-reports')),

  saveCustomReport: (body: {
    reportCode: string;
    reportName: string;
    baseReportType?: string;
    configJson: Record<string, unknown>;
  }) => unwrap(api.post<ApiResponse<FinCustomReport>>('/v1/finance/enterprise/custom-reports', body)),

  runCustomReport: (id: number) =>
    unwrap(api.get<ApiResponse<FinancialReport>>(`/v1/finance/enterprise/custom-reports/${id}/run`)),

  cfoOverview: () =>
    unwrap(api.get<ApiResponse<import('@/types/finance').FinanceCfoOverview>>('/v1/finance/platform/cfo-overview')),

  healthCenter: () =>
    unwrap(api.get<ApiResponse<import('@/types/finance').FinanceHealthCenter>>('/v1/finance/platform/health')),

  setupWizard: () =>
    unwrap(api.get<ApiResponse<import('@/types/finance').FinanceSetupWizard>>('/v1/finance/platform/setup/wizard')),

  completeSetupStep: (stepCode: string, body?: Record<string, unknown>) =>
    unwrap(
      api.post<ApiResponse<import('@/types/finance').FinanceSetupWizard>>(
        `/v1/finance/platform/setup/wizard/steps/${stepCode}/complete`,
        body ?? {},
      ),
    ),

  listCloseRuns: () =>
    unwrap(api.get<ApiResponse<import('@/types/finance').FinCloseRun[]>>('/v1/finance/platform/close/runs')),

  listCloseTasks: (runId: number) =>
    unwrap(api.get<ApiResponse<import('@/types/finance').FinCloseTask[]>>(
      `/v1/finance/platform/close/runs/${runId}/tasks`,
    )),

  startCloseRun: (params: { closeType: string; fiscalYear: number; fiscalPeriod: number; notes?: string }) =>
    unwrap(api.post<ApiResponse<import('@/types/finance').FinCloseRun>>('/v1/finance/platform/close/runs', null, { params })),

  completeCloseTask: (runId: number, taskId: number, evidenceNote?: string) =>
    unwrap(
      api.post<ApiResponse<import('@/types/finance').FinCloseTask>>(
        `/v1/finance/platform/close/runs/${runId}/tasks/${taskId}/complete`,
        null,
        { params: { evidenceNote } },
      ),
    ),

  listBusinessRules: () =>
    unwrap(api.get<ApiResponse<import('@/types/finance').FinBusinessRule[]>>('/v1/finance/platform/business-rules')),

  saveBusinessRule: (body: {
    ruleCode: string;
    ruleName: string;
    entityType: string;
    conditionJson: Record<string, unknown>;
    actionJson: Record<string, unknown>;
  }) => unwrap(api.post<ApiResponse<import('@/types/finance').FinBusinessRule>>('/v1/finance/platform/business-rules', body)),

  evaluateBusinessRule: (body: { entityType: string; context: Record<string, unknown> }) =>
    unwrap(api.post<ApiResponse<import('@/types/finance').BusinessRuleEvaluation>>('/v1/finance/platform/business-rules/evaluate', body)),

  platformCashForecast: (days = 30) =>
    unwrap(api.get<ApiResponse<import('@/types/finance').FinanceCashForecast>>('/v1/finance/platform/cash-forecast', { params: { days } })),

  collectionsCenter: () =>
    unwrap(api.get<ApiResponse<import('@/types/finance').FinanceCollectionsCenter>>('/v1/finance/platform/collections')),

  addCollectionActivity: (body: {
    accountsReceivableId: number;
    activityType: string;
    notes?: string;
    promiseDate?: string;
    promiseAmount?: number;
  }) => unwrap(api.post<ApiResponse<unknown>>('/v1/finance/platform/collections/activities', body)),

  runCollectionsDunning: () =>
    unwrap(api.post<ApiResponse<{ noticesSent: number }>>('/v1/finance/platform/collections/dunning/run')),

  automationCenter: () =>
    unwrap(api.get<ApiResponse<import('@/types/finance').FinanceAutomationCenter>>('/v1/finance/platform/automation')),

  runAutomationJob: (jobCode: string) =>
    unwrap(api.post<ApiResponse<import('@/types/finance').AutomationRun>>(`/v1/finance/platform/automation/jobs/${jobCode}/run`)),

  listDocumentCaptures: () =>
    unwrap(api.get<ApiResponse<import('@/types/finance').FinDocumentCapture[]>>('/v1/finance/platform/documents')),

  captureDocument: (body: { documentType: string; fileName: string; rawText?: string }) =>
    unwrap(api.post<ApiResponse<import('@/types/finance').FinDocumentCapture>>('/v1/finance/platform/documents/capture', body)),

  approveDocumentCapture: (id: number, reviewNotes?: string) =>
    unwrap(api.post<ApiResponse<import('@/types/finance').FinDocumentCapture>>(`/v1/finance/platform/documents/${id}/approve`, null, { params: { reviewNotes } })),

  rejectDocumentCapture: (id: number, reviewNotes?: string) =>
    unwrap(api.post<ApiResponse<import('@/types/finance').FinDocumentCapture>>(`/v1/finance/platform/documents/${id}/reject`, null, { params: { reviewNotes } })),

  platformAnalytics: () =>
    unwrap(api.get<ApiResponse<import('@/types/finance').FinanceAnalytics>>('/v1/finance/platform/analytics')),

  platformCompliance: () =>
    unwrap(api.get<ApiResponse<import('@/types/finance').FinanceCompliance>>('/v1/finance/platform/compliance')),

  activateCompliancePack: (countryCode: string, packCode: string) =>
    unwrap(api.post<ApiResponse<import('@/types/finance').FinComplianceProfile>>('/v1/finance/platform/compliance/activate', null, { params: { countryCode, packCode } })),

  platformAuditTrail: (limit = 50) =>
    unwrap(api.get<ApiResponse<import('@/types/finance').FinAuditEntry[]>>('/v1/finance/platform/audit-trail', { params: { limit } })),

  integrationHealth: () =>
    unwrap(api.get<ApiResponse<import('@/types/finance').FinanceIntegrationHealth>>('/v1/finance/platform/integrations/health')),

  workspaceManifest: () =>
    unwrap(api.get<ApiResponse<import('@/types/finance').FinanceWorkspaceManifest>>('/v1/finance/platform/workspaces')),
};
